import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Theme, Typography, useMediaQuery } from "@mui/material";
import { toCurrency } from "@helpers";
import { InvestmentChart, PHONE_CHART_HEIGHT } from "@components";
import { getStartDateForTransactions } from "@helpers/getStartDateForTransactions.tsx";
import {
  MobileRangeControl,
  useChartControls,
} from "@components/Chart/ChartControls";
import { useAccountChartData } from "../hooks/useAccountChartData";

const ChartTitleWrapper = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginBottom: theme.spacing(5),
  [theme.breakpoints.down("md")]: {
    marginBottom: theme.spacing(0),
  },
}));

interface Props {
  accountId: number;
  balance: number;
  currency: string;
}

export const SingleAccountPageChart = ({
  accountId,
  balance,
  currency,
}: Props) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { rangeProps } = useChartControls();

  const {
    data: chartData,
    isPending,
    isFetching,
  } = useAccountChartData({
    accountId,
    startDate: rangeProps.range.startDate,
    endDate: getStartDateForTransactions(rangeProps.range.endDate),
    displayCurrency: currency,
  });

  return (
    <InvestmentChart
      isFetching={isPending || isFetching}
      title={
        <ChartTitleWrapper>
          <Typography variant="body2">{t("accountChart.subtitle")}</Typography>
          <Typography variant={isSm ? "h4" : "h2"}>
            {toCurrency(balance, currency)}
          </Typography>
        </ChartTitleWrapper>
      }
      data={chartData}
      rangeProps={rangeProps}
      hideRangeSelector={isSm}
      figureAction={isSm ? <MobileRangeControl /> : undefined}
      showNavigator={false}
      chartHeight={isSm ? PHONE_CHART_HEIGHT : undefined}
      currency={currency}
    />
  );
};
